/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { GreenMarker } from '../../../../../../common/components/GreenMarker/GreenMarker';
import { Linkify } from '../../../../../../common/components/Linkify';
import { useDetectOutsideClicks } from '../../../../../../common/hooks/use-detect-outside-clicks';
import { useGreenCueMarkers } from '../../../../../../common/hooks/use-green-cue-markers';
import { ZoomImageModal } from '../../../../../../common/modals/ZoomImageModal/ZoomImageModal';
import { useViewActions } from '@mm/state/actions/viewActions';
import { SlideoutMenu } from '../../../../../components/SlideoutMenu/components/SlideoutMenu';
import { Heading } from '@mm/components';
import { KnownSiteType } from '../shared';
import styles from './CustomerChatInstallationGuideSlideoutMenu.module.css';
import type { CustomerChatInstallationGuideSlideoutMenuInstructionsType } from './CustomerChatInstallationGuideSlideoutMenuInstructions';
import { LinkTypes, PromoterTypes } from '@mm/state/model/PromoterModel';
import classNames from 'classnames';

type Props = {
  siteType: KnownSiteType;
  promoterJSCode: string;
  promoterType: LinkTypes;
  onCopyClick?: () => void;
};

export const CustomerChatInstallationGuideSlideoutMenu = ({
  siteType,
  promoterJSCode,
  promoterType,
  onCopyClick,
}: Props) => {
  const { hideSlideoutMenu } = useViewActions();

  const [zoomImage, setZoomImage] = useState<string>();
  const [wrapperRef, setWrapperRef] = useState<HTMLElement | null>(null);
  const [allInstructions, setAllInstructions] =
    useState<CustomerChatInstallationGuideSlideoutMenuInstructionsType>();

  const { isWebChatActivated } = useGreenCueMarkers();
  const isGetEmails = promoterType === PromoterTypes.GETEMAILS;

  useEffect(() => {
    (async () => {
      const data = await await import(
        /* webpackChunkName: "CustomerChatInstallationGuideSlideoutMenuInstructions" */
        './CustomerChatInstallationGuideSlideoutMenuInstructions'
      );
      setAllInstructions(
        isGetEmails
          ? data.EmailDetectorInstallationGuideSlideoutMenuInstructions
          : data.CustomerChatInstallationGuideSlideoutMenuInstructions,
      );
    })();
  }, []);

  const setRef = (ref: HTMLElement) => {
    setWrapperRef(ref);
  };
  useDetectOutsideClicks(() => {
    if (!zoomImage) {
      hideSlideoutMenu();
    }
  }, wrapperRef);

  const instructions = allInstructions ? allInstructions[siteType] : null;
  const content =
    !!instructions &&
    instructions.map((item, i) => {
      const step = typeof item === 'string' ? item : item.step;
      const image = typeof item === 'string' ? null : item.image;
      const substeps = typeof item === 'string' ? null : item.substeps;
      const includeCode = typeof item === 'string' ? null : item.includeCode;
      return (
        <>
          <div key={i} className={styles.step}>
            <div className={styles.step_numeral}>{i + 1}</div>
            <div className={styles.step_text}>
              <Linkify>{step}</Linkify>
            </div>
            {includeCode && (
              <>
                <div />
                <div className={styles.codeContainer}>
                  <textarea
                    readOnly
                    className={classNames(styles.codeBlock, {
                      [styles.smallCodeBlock]: isGetEmails,
                    })}
                    value={promoterJSCode}
                    onFocus={e => e.target.select()}
                  />
                  <CopyToClipboard text={promoterJSCode}>
                    <PrimaryButton
                      size="sm"
                      className={styles.codeBlockCopyButton}
                      onClick={onCopyClick}
                    >
                      Copy Code
                    </PrimaryButton>
                  </CopyToClipboard>
                </div>
              </>
            )}
            {image && (
              <>
                <div />
                <img
                  className={styles.step_image}
                  onClick={() => setZoomImage(image)}
                  src={image}
                />
              </>
            )}
          </div>
          {substeps && (
            <div className={styles.substeps}>
              {substeps.map((substep, substepIdx) => (
                <div key={substep} className={styles.step}>
                  <div className={styles.step_numeral}>
                    {String.fromCharCode('a'.charCodeAt(0) + substepIdx)}
                  </div>
                  <div className={styles.step_text}>
                    <Linkify>{substep}</Linkify>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      );
    });

  return (
    <>
      {zoomImage && (
        <ZoomImageModal
          displayImageSrc={zoomImage}
          closeModal={() => setZoomImage(undefined)}
          fileName=""
          personName=""
          zIndex={10000004}
        />
      )}

      <SlideoutMenu setRef={setRef}>
        <SlideoutMenu.Header>
          <Heading className={styles.heading} size={3}>
            {!isWebChatActivated && <GreenMarker />}
            <div>{siteType} Installation Guide</div>
          </Heading>
        </SlideoutMenu.Header>
        <SlideoutMenu.Content>
          <div className={styles.description}>
            Take the following steps to install{' '}
            {isGetEmails ? 'an X-Ray Lead Generator' : 'a chat widget'} on your
            website:
          </div>
          {content}
        </SlideoutMenu.Content>
      </SlideoutMenu>
    </>
  );
};
